/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useContext, useState } from 'react';

import { StatusCode } from '../api/enumerations';
import {
  associateWorkOrderRegistry,
  checkArchivedRegistry,
  checkWorkOrderRegistry,
  getAssociatedList,
} from '../api/registryAssociation';
import {
  AssociateRegistryData,
  AssociationWOData,
  CheckWorkOrderRegistryParams,
} from '../api/registryAssociation/types';
import { GlobalContext } from '../context/global';
import useErrorMessage from './useErrorMessage';

export type AssociateProps = {
  data: AssociateRegistryData;
  successMessage: string;
  callback: () => void | Promise<void>;
};

interface AssociateWorkOrderProps {
  getArchivedMatchRegistryWorkOders: (
    params: CheckWorkOrderRegistryParams
  ) => Promise<AssociationWOData[]>;
  getMatchRegistryWorkOders: (
    params: CheckWorkOrderRegistryParams
  ) => Promise<AssociationWOData[]>;
  handleWorkOrderAssociation: (props: AssociateProps) => Promise<void>;
  getAssociatedListCallback: (osId: number) => Promise<AssociationWOData[]>;
  loading: boolean;
  associateLoading: boolean;
}

export function useAssociateWorkOrder(): AssociateWorkOrderProps {
  const [loading, setLoading] = useState(false);
  const [associateLoading, setAssociateLoading] = useState(false);

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);

  const { getErrorMessage } = useErrorMessage();

  const getArchivedMatchRegistryWorkOders = useCallback(
    async (
      params: CheckWorkOrderRegistryParams
    ): Promise<AssociationWOData[]> => {
      try {
        setLoading(true);
        const response = await checkArchivedRegistry(params);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (!response.data || response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }
        return response.data;
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const getMatchRegistryWorkOders = useCallback(
    async (
      params: CheckWorkOrderRegistryParams
    ): Promise<AssociationWOData[]> => {
      try {
        setLoading(true);
        const response = await checkWorkOrderRegistry(params);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (!response.data || response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }
        return response.data;
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const handleWorkOrderAssociation = useCallback(
    async ({
      callback,
      data,
      successMessage,
    }: AssociateProps): Promise<void> => {
      try {
        setAssociateLoading(true);
        const response = await associateWorkOrderRegistry(data);

        if (response.detail.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }
        setSnackbarMessage(successMessage);
        setErrorMessage(false);
        setOpenSnackbar(true);
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
      } finally {
        callback();
        setAssociateLoading(false);
      }
    },
    []
  );

  const getAssociatedListCallback = useCallback(
    async (osId: number): Promise<AssociationWOData[]> => {
      try {
        const response = await getAssociatedList(osId);
        if (response.detail?.description) {
          throw new Error(response.detail.description);
        }

        if (response.detail.status_code !== StatusCode.OK) {
          throw new Error('Algo deu errado, tente novamente.');
        }

        return response.data;
      } catch (error) {
        setSnackbarMessage(getErrorMessage(error));
        setErrorMessage(true);
        setOpenSnackbar(true);
        return [];
      }
    },
    []
  );

  return {
    getMatchRegistryWorkOders,
    getArchivedMatchRegistryWorkOders,
    handleWorkOrderAssociation,
    getAssociatedListCallback,
    loading,
    associateLoading,
  };
}
